import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import logo from "../../../../assets/images/logo-rewind.png";
import { useTranslation } from "react-i18next";
import { DropdownContent } from "../../../../types/types";
import { getProjects } from "../../../../api";
import Dropdown from "../../Dropdown";
import Button from "../../../ui/Button";
import { useGlobalState } from "../../../../context/useGlobalState";
import TextInput from "../../../ui/TextInput";
import { useAudioEditor } from "../../../../context/AudioEditorContext";
import AudioEditorNormalizeExportOptions from "../AudioEditorNormalizeExportOptions";
import AudioEditorTranscriptFields, {
    TranscriptEntry,
    VoiceOption,
    buildEntries,
    linesByClip,
} from "../AudioEditorTranscriptFields";
import { collectClipCards } from "../utils";
import { getVoices } from "../../../../api";
import studio from "../../../modals/StudioModal.module.css";
import { ModalPortal } from "../../../modals/ModalPortal";

export interface AudioEditorAddToProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onClick: () => void;
    placeholder?: string;
}

const AudioEditorAddToProjectModal: React.FC<AudioEditorAddToProjectModalProps> = ({
    isOpen,
    onClose,
    onClick,
    placeholder,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const { setAddToProjectSettings, isProcessing, tracks, sessionAudio } = useAudioEditor();

    // When the editor was opened from an existing capsule we re-save onto that row
    // instead of adding another one to the project on every round trip.
    const isUpdatingExisting = Boolean(sessionAudio?.audioAIId);

    // One card per block of speech, for every clip of the mix. The audio being edited is
    // itself one of those clips, so it needs no card of its own — and there is nothing to
    // reconcile between what the timeline shows and what the replies say.
    const cards = useMemo(() => collectClipCards(tracks), [tracks]);
    const [transcriptEntries, setTranscriptEntries] = useState<TranscriptEntry[]>([]);
    const [voiceOptions, setVoiceOptions] = useState<VoiceOption[]>([]);

    const [audioName, setAudioName] = useState<string>("");
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [addToProjectTriggered, setAddToProjectTriggered] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [projects, setProjects] = useState<DropdownContent[]>([]);

    const token = localStorage.getItem("token") || "";

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest(".rw-select__menu") || target.closest(".rw-select__menu-portal")) {
                return;
            }
            if (modalContentRef.current && !modalContentRef.current.contains(target)) {
                onClose();
            }
        },
        [onClose],
    );

    const languagesAIOptions: DropdownContent[] =
        data?.languagesAI?.map((language) => ({
            value: language.code,
            label: language.label,
        })) || [];

    // Rebuilt each time the modal opens, so it reflects the timeline as it stands now.
    useEffect(() => {
        if (isOpen) setTranscriptEntries(buildEntries(cards));
    }, [isOpen, cards]);

    // Voices to choose from, so an imported recording can be attributed instead of
    // falling back on "unknown voice" at translation time.
    useEffect(() => {
        if (!isOpen || voiceOptions.length > 0) return;
        const token = localStorage.getItem("token");
        if (!token) return;
        getVoices({ token })
            .then((voices) =>
                setVoiceOptions(
                    // Only the voices the company has activated, the same set the
                    // translation screen keeps. /get-voices returns the whole catalogue,
                    // so without this the editor offers voices that screen then drops —
                    // the block ends up attributed to a voice nobody can map it to, and
                    // it silently disappears from the mapping columns.
                    (voices || [])
                        .filter((voice: any) => voice.isActivated === true)
                        .map((voice: any) => ({
                            value: voice.id,
                            label: voice.label || voice.name,
                            // Kept so a capsule generated from a clone or a designed voice
                            // can be matched back to its entry: those store the clone or
                            // prompt row id, not the VoiceAI id this list is keyed by.
                            voiceCloneDbId: voice.voiceCloneDbId ?? null,
                            promptVoiceDbId: voice.promptVoiceDbId ?? null,
                        })),
                ),
            )
            .catch(() => setVoiceOptions([]));
    }, [isOpen, voiceOptions.length]);

    useEffect(() => {
        if (isOpen && sessionAudio) {
            setAudioName((current) => current || sessionAudio.name);
            setSelectedContentLanguage((current) => current || sessionAudio.language);
        }
    }, [isOpen, sessionAudio]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        if (!isOpen) {
            setAddToProjectTriggered(false);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    useEffect(() => {
        if (!isProcessing && isOpen && addToProjectTriggered) {
            onClose();
        }
    }, [isProcessing, isOpen, addToProjectTriggered, onClose]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                if (!token) {
                    setError(t("noToken"));
                    return;
                }
                const response = await getProjects({ token });
                if (!response) {
                    setError(t("failedToFetchProjects"));
                } else {
                    const projectsList = response.map((project: { id: string; name: string }) => ({
                        label: project.name,
                        value: project.id,
                    }));
                    setProjects(projectsList);
                }
            } catch {
                setError(t("errorFetchingAudio"));
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [token, t]);

    if (!isOpen) return null;

    const modalTitle = isUpdatingExisting ? t("updateAudioInProjectTitle") : t("addAudioToProjectTitle");
    const modalSubtitle = isUpdatingExisting ? t("updateAudioInProjectSubtitle") : t("addAudioToProjectSubtitle");
    // Updating in place keeps the row's project, so only a name and a language are needed.
    const canSubmit = Boolean(audioName.trim() && selectedContentLanguage && (isUpdatingExisting || selectedProject));

    const handleAddtoProject = () => {
        if (!canSubmit || isProcessing) return;
        setAddToProjectSettings({
            name: audioName.trim(),
            language: selectedContentLanguage!,
            projectId: selectedProject,
            targetAudioAIId: sessionAudio?.audioAIId ?? null,
            // The only thing the save needs. The composition stores these blocks on their
            // clips; the transcript of the audio and its dialog lines are both derived
            // from them server-side, so nothing can drift between the three.
            linesByClip: linesByClip(transcriptEntries),
        });
        onClick();
        setAddToProjectTriggered(true);
    };

    return (
        <ModalPortal>
            <div className={studio.overlayTop}>
                <div
                    className={[
                        studio.projectModalContent,
                        studio.addAudioModal,
                        transcriptEntries.length > 0 ? studio.addAudioModalWide : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    ref={modalContentRef}
                >
                    <div className={studio.logoWrap}>
                        <img src={logo} alt="Logo" />
                    </div>
                    <h1 className={studio.projectModalTitle}>{modalTitle}</h1>
                    <p className={studio.projectModalSub}>{modalSubtitle}</p>

                    <div className={studio.formStack}>
                        <label className={studio.aaField}>
                            <span className={studio.aaFieldLabel}>{t("audioNameLabel")}</span>
                            <TextInput
                                placeholder={t("audioNamePlaceholder")}
                                onChange={(e) => setAudioName(e.target.value)}
                                value={audioName}
                                disabled={isProcessing}
                                fullWidth
                                size="small"
                            />
                        </label>
                        {!isUpdatingExisting && (
                            <div className={studio.aaField}>
                                <Dropdown
                                    label={t("project")}
                                    options={projects}
                                    value={selectedProject}
                                    onChange={(value) => {
                                        setSelectedProject(value);
                                        setSelectedContentLanguage(null);
                                    }}
                                    placeholder={t("selectProject")}
                                    loading={loading}
                                    error={!!error}
                                    disabled={isProcessing}
                                    fullWidth
                                    isWithoutMargin
                                />
                            </div>
                        )}
                        <div className={studio.aaField}>
                            <Dropdown
                                label={t("language")}
                                options={languagesAIOptions}
                                value={selectedContentLanguage}
                                onChange={(value) => {
                                    setSelectedContentLanguage(value);
                                }}
                                placeholder={t("selectALanguage")}
                                loading={false}
                                error={false}
                                disabled={isProcessing || (!isUpdatingExisting && !selectedProject)}
                                fullWidth
                                isWithoutMargin
                            />
                        </div>
                        <AudioEditorTranscriptFields
                            entries={transcriptEntries}
                            onChange={setTranscriptEntries}
                            voiceOptions={voiceOptions}
                            disabled={isProcessing}
                        />
                        <AudioEditorNormalizeExportOptions />
                    </div>

                    <div className={studio.buttonsCentered}>
                        <Button
                            variant="primary"
                            shape="pill"
                            block
                            label={
                                isProcessing
                                    ? t("processing") || "Processing..."
                                    : isUpdatingExisting
                                      ? t("updateInProject")
                                      : t("addToProject")
                            }
                            onClick={handleAddtoProject}
                            disabled={isProcessing || !canSubmit}
                        />
                        <Button
                            variant="cancel"
                            shape="pill"
                            block
                            label={t("close")}
                            onClick={onClose}
                            disabled={isProcessing}
                        />
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default AudioEditorAddToProjectModal;
